/*
 * Which version of a PDF page image the client should be showing, and whether
 * what it is showing is final.
 *
 * ---------------------------------------------------------------------------
 * THE PROBLEM THIS SOLVES
 * ---------------------------------------------------------------------------
 * A PDF passes through two library-stage transformations before it is the file
 * that gets printed (`docs/architecture/pdf-pipeline.md`):
 *
 *   original  --PREPARE-->  prepared  --STANDARDIZE-->  standardized
 *             Resource.prepared        Cover.standardized / Body.standardized
 *
 * The page-image endpoints (`/resource/imageurl`, `/book/imageurl`) pick a
 * version **server-side and do not report which one they chose** —
 * `docs/contracts/shared/Notifications.md` §9 states it outright: "the URL shape
 * you call is identical either way".
 *
 * Left at that, three things are wrong:
 *
 *   1. The client cannot know when a better version has appeared, so an image
 *      fetched at `prepared: WAITING` is an ORIGINAL render that stays on
 *      screen for the whole session.
 *   2. The user is shown an unverified render with nothing saying so. An
 *      original can differ visibly from the prepared file — unflattened layers,
 *      substituted fonts, non-printing objects still visible.
 *   3. Even a re-request may change nothing, because the cached `<img>` is
 *      keyed on an id that does not mention the version.
 *
 * So the client models the stage itself, from the two status fields it already
 * caches, and drives its own refresh from that. Requested of the server:
 * report the version actually served (`shared/PdfArtifactImages.md` §5.1), at
 * which point this stops being an inference.
 *
 * ---------------------------------------------------------------------------
 * NO ANGULAR IMPORTS -- DELIBERATE
 * ---------------------------------------------------------------------------
 * Same reasoning as `resource-image-cache.ts` and `service-data/http-error.ts`:
 * it keeps the rules reachable from the layer-0 smoke tests, which need no
 * browser and no TestBed. Covered by `tests/PdfArtifactStage.smoke.ts`.
 *
 * Anything that needs translated text lives in the component that renders it
 * (`PreviewQualityIndicatorComponent`), not here — importing `LanguagePipe`
 * would drag Angular in and cost the test.
 */

/** Which artefact a rendered page image came from. */
export type PdfImageStage = 'NONE' | 'ORIGINAL' | 'PREPARED' | 'STANDARDIZED';

/** What, if anything, the UI should say about work still outstanding. */
export type PdfProcessingNotice = 'NONE' | 'PREPARING' | 'STANDARDIZING' | 'ERROR';

/** The pipeline status value meaning "done". */
export const COMPLETE = 'COMPLETE';
/** The pipeline status value meaning "failed, and a retry will not help". */
export const ERROR = 'ERROR';

/**
 * Everything needed to decide which image to show, gathered from whichever
 * objects the caller happens to hold.
 *
 * Callers pass `Resource.prepared` and — on the Book side only —
 * `Cover.standardized` / `Body.standardized`.
 */
export interface PdfArtifactStatus {
  /** The Resource id being displayed. 0 / absent means no PDF uploaded yet. */
  resourceId?: number;

  /** `Resource.prepared`. Exists for Drafts and Books alike. */
  prepared?: string;

  /**
   * `Cover.standardized` / `Body.standardized`.
   *
   * **`undefined` means "this artefact has no standardize stage"** — i.e. a
   * `DraftCover` or `DraftBody`, which genuinely have no such field. That is
   * different from `''`, which is what `new Cover(...)` initialises the field
   * to before the server value arrives: a Book cover that has the stage but
   * whose status is not yet known.
   */
  standardized?: string;

  /**
   * Override for the "does this artefact standardize at all?" question.
   *
   * Defaults to `standardized !== undefined`, which is right almost always.
   * Pass it explicitly when the caller *knows* it is rendering a Book but the
   * `Cover`/`Body` row has not arrived yet — otherwise a Book would be treated
   * as a Draft for as long as its row is missing, and a PREPARED render would
   * be labelled final.
   */
  standardizes?: boolean;
}

/** Is there a PDF at all? `resource_ID` 0 means "nothing uploaded yet". */
export function hasResource(status: PdfArtifactStatus | null | undefined): boolean {
  return !!(status && typeof status.resourceId === 'number' && status.resourceId > 0);
}

/**
 * Does this artefact have a STANDARDIZE stage?
 *
 * Books do (`Cover` / `Body`). Drafts do not (`DraftCover` / `DraftBody` have
 * no `standardized` field at all). This single question is why the Draft
 * preview and the Book preview cannot be one component: it decides what
 * "finished" means.
 */
export function hasStandardizeStage(status: PdfArtifactStatus | null | undefined): boolean {
  if (!status) { return false; }
  if (typeof status.standardizes === 'boolean') { return status.standardizes; }
  return status.standardized !== undefined && status.standardized !== null;
}

/**
 * The best version that exists **right now**.
 *
 * Deliberately ordered best-first, and deliberately does NOT consult `ERROR`:
 * a failed STANDARDIZE still leaves a perfectly good prepared file to show.
 * Reporting the failure is {@link processingNotice}'s job, not this one's.
 */
export function bestAvailableStage(status: PdfArtifactStatus | null | undefined): PdfImageStage {
  if (!hasResource(status)) { return 'NONE'; }
  if (hasStandardizeStage(status) && status.standardized === COMPLETE) { return 'STANDARDIZED'; }
  if (status.prepared === COMPLETE) { return 'PREPARED'; }
  // The original is always renderable once the object is in S3 — the image
  // endpoint does not require `prepared`, per Notifications.md §9. So there is
  // always something to show; the only question is whether it is trustworthy.
  return 'ORIGINAL';
}

/**
 * The version this artefact will *eventually* reach.
 *
 * For a Book that is STANDARDIZED; for a Draft it is PREPARED, because there
 * is no further stage. Everything about "is this preliminary?" reduces to
 * comparing against this.
 */
export function finalStage(status: PdfArtifactStatus | null | undefined): PdfImageStage {
  if (!hasResource(status)) { return 'NONE'; }
  return hasStandardizeStage(status) ? 'STANDARDIZED' : 'PREPARED';
}

/**
 * Is the image currently on screen a stand-in for a better one still to come?
 *
 * This is what drives the indicator. `NONE` is not preliminary — there is no
 * image, which is a different message.
 */
export function isPreliminary(status: PdfArtifactStatus | null | undefined): boolean {
  const best = bestAvailableStage(status);
  if (best === 'NONE') { return false; }
  return best !== finalStage(status);
}

/**
 * Is anything still expected to change?
 *
 * Used to decide whether a component needs to keep listening at all. `ERROR`
 * counts as "still expected": an operator can correct the upload or re-request
 * the stage, and the row will then move again. Subscribing costs one `Subject`
 * subscription, so the bias is deliberately towards listening.
 */
export function awaitsBetterImage(status: PdfArtifactStatus | null | undefined): boolean {
  if (!hasResource(status)) { return false; }
  if (status.prepared !== COMPLETE) { return true; }
  return hasStandardizeStage(status) && status.standardized !== COMPLETE;
}

/**
 * What the UI should say about outstanding work.
 *
 * ── On the product owner's "show a message instead" ──────────────────────
 * The stated Book rule is: standardized if COMPLETE, else prepared if
 * COMPLETE, else *"show a message saying that the PDF is being prepared"*.
 *
 * We show the message **and** keep the ORIGINAL render underneath it, rather
 * than replacing the image with text. An empty panel where a page could be is
 * a worse answer to "what did I upload?" than an honest, labelled preview —
 * and the label carries exactly the sentence that was asked for. If that
 * judgement is ever reversed, this function is the only thing to change: a
 * caller that wants the strict reading suppresses the image whenever this
 * returns `PREPARING`.
 */
export function processingNotice(status: PdfArtifactStatus | null | undefined): PdfProcessingNotice {
  if (!hasResource(status)) { return 'NONE'; }
  if (status.prepared === ERROR) { return 'ERROR'; }
  if (hasStandardizeStage(status) && status.standardized === ERROR) { return 'ERROR'; }
  if (status.prepared !== COMPLETE) { return 'PREPARING'; }
  if (hasStandardizeStage(status) && status.standardized !== COMPLETE) { return 'STANDARDIZING'; }
  return 'NONE';
}

// ── Cache keys ──────────────────────────────────────────────────────────────

/**
 * One character per stage, appended to a hidden-image cache id.
 *
 * ⚠ **These must stay single characters and must stay after a `-`.**
 * `isElementIdForResource` in `resource-image-cache.ts` matches
 * `resource-<id>` exactly or `resource-<id>-` as a prefix, so a suffixed id
 * like `resource-12-1-288-p` is still invalidated correctly while
 * `resource-123-...` still is not. Anything that changed that shape would
 * silently break invalidation in one direction or the other, and neither
 * direction reports an error.
 */
const STAGE_SUFFIX: { [stage: string]: string } = {
  NONE: 'n',
  ORIGINAL: 'o',
  PREPARED: 'p',
  STANDARDIZED: 's',
};

export function stageSuffix(stage: PdfImageStage): string {
  return STAGE_SUFFIX[stage] || 'n';
}

/**
 * Append the stage to a cache id.
 *
 * This is the mechanism that makes an improvement actually appear. Without it,
 * `resource-12-1-288` cached at ORIGINAL is found again after preparation
 * completes, treated as a hit, and the better render is never requested — or,
 * if it is requested, an unchanged signed URL lets the browser serve the same
 * bytes from its own cache. Putting the stage in the key sidesteps both,
 * without depending on the server changing the URL between versions
 * (`shared/PdfArtifactImages.md` Q3).
 */
export function stagedImageId(baseId: string, stage: PdfImageStage): string {
  if (!baseId) { return baseId; }
  return baseId + '-' + stageSuffix(stage);
}

/**
 * Snapshot of the inputs a rendered image depended on.
 *
 * Components compare the previous snapshot with the current one and only
 * re-request when it differs. `updateNotifier` fires for **every** change to
 * **any** row of that type — a title edit, a shift adjustment, another user's
 * action — so without this a busy organisation would re-request every visible
 * thumbnail on every unrelated update.
 */
export function stageSignature(status: PdfArtifactStatus | null | undefined): string {
  if (!status) { return 'none'; }
  return [
    hasResource(status) ? status.resourceId : 0,
    bestAvailableStage(status),
    processingNotice(status),
  ].join('|');
}

